import { DocumentationItemConfigurationV2 } from "@supernova-studio/model";
import { LocalDocsPage, LocalDocsPageGroup } from "../../../sync/types";
import { VersionRoomDocsPage, VersionRoomDocsPageGroup } from "../../../yjs/version-room/types";
import { DTODocumentationPageV2 } from "../../dto";
import {
  documentationItemConfigurationToDTOV2,
  getDtoDefaultItemConfigurationV2,
} from "./documentation-item-configuration-v2-to-dto";
import { applyPrivacyConfigurationToNestedItems, buildDocPagePublishPaths } from "./documentation-page-to-dto-utils";

type DocsPage = LocalDocsPage | VersionRoomDocsPage;
type DocsPageGroup = LocalDocsPageGroup | VersionRoomDocsPageGroup;

// The fact that DTO conversion is located here instead of main backend code is due to the fact
// that we store page and group data in YJS documents in the same way as they are stored in the database.
// Therefore, we need to expose this conversion to the client so that it can consume data from YJS documents.
//
// Please do not put more DTO conversion here unless you know what you're doing.

export function documentationPageToDTOV2(
  page: DocsPage,
  groups: DocsPageGroup[],
  routingVersion: string,
  pageLiveblocksRoomIdMap: Map<string, string>
): DTODocumentationPageV2 {
  const pathsMap = buildDocPagePublishPaths(groups, [page], routingVersion);
  return _documentationPageToDTOV2(page, pathsMap, pageLiveblocksRoomIdMap);
}

export function documentationPagesToDTOV2(
  pages: DocsPage[],
  groups: DocsPageGroup[],
  routingVersion: string,
  pageLiveblocksRoomIdMap: Map<string, string>
): DTODocumentationPageV2[] {
  const pathsMap = buildDocPagePublishPaths(groups, pages, routingVersion);
  return pages.map(page => _documentationPageToDTOV2(page, pathsMap, pageLiveblocksRoomIdMap));
}

export function documentationPagesFixedConfigurationToDTOV2(
  pages: DocsPage[],
  groups: DocsPageGroup[],
  routingVersion: string,
  pageLiveblocksRoomIdMap: Map<string, string>
): DTODocumentationPageV2[] {
  const pathsMap = buildDocPagePublishPaths(groups, pages, routingVersion);

  const { pages: fixedPages } = applyPrivacyConfigurationToNestedItems<
    DocsPage,
    DocsPageGroup,
    DocumentationItemConfigurationV2
  >(pages, groups, getDtoDefaultItemConfigurationV2);

  return fixedPages.map(page => _documentationPageToDTOV2(page, pathsMap, pageLiveblocksRoomIdMap));
}

function _documentationPageToDTOV2(
  page: DocsPage,
  pagePathMap: Map<string, string>,
  pageLiveblocksRoomIdMap: Map<string, string>
): DTODocumentationPageV2 {
  let path = pagePathMap.get(page.persistentId);
  if (!path) throw new Error(`Path for page ${page.persistentId} was not calculated`);

  if (path.startsWith("/")) path = path.substring(1);

  const liveblocksRoomId = pageLiveblocksRoomIdMap.get(page.persistentId);

  return {
    id: "to_be_removed",
    designSystemVersionId: page.designSystemVersionId,
    persistentId: page.persistentId,
    shortPersistentId: page.shortPersistentId,
    title: page.meta.name,
    slug: page.slug,
    userSlug: page.userSlug,
    configuration: documentationItemConfigurationToDTOV2(page.data.configuration),
    createdAt: page.createdAt,
    updatedAt: page.updatedAt,
    path: path,
    type: "Page",
    ...(liveblocksRoomId && { liveblocksRoomId }),
  };
}
